/**
 * HTTP client — mirrors the trader UI's client, with one difference: the shop
 * is a SEPARATE ORIGIN from the API, so every request is prefixed with
 * `VITE_API_BASE` (empty in dev → goes through the vite proxy).
 *
 * - Reads the bearer token from `TokenStore` (localStorage, key `piggy.token`,
 *   shared with the trader app so a future shared subdomain stays consistent).
 * - On 401, clears the token and dispatches `piggy:auth-expired`; AuthContext
 *   listens and routes to /login.
 * - Throws `ApiError` for non-2xx responses.
 */
const TOKEN_KEY = "piggy.token";
const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export class TokenStore {
  static get(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }
  static set(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }
  static clear(): void {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** Encode `body` as application/x-www-form-urlencoded (login). */
  form?: boolean;
  /** Suppress the auth-expired dispatch on 401 (used by /auth/jwt/login). */
  skipAuthExpiredOn401?: boolean;
  headers?: Record<string, string>;
  /** Skip Authorization header (public endpoints: branding, catalogue). */
  noAuth?: boolean;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.form) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(opts.body as Record<string, string>)) {
        if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
      }
      body = params.toString();
      headers["Content-Type"] = "application/x-www-form-urlencoded";
    } else {
      body = JSON.stringify(opts.body);
      headers["Content-Type"] = "application/json";
    }
  }

  if (!opts.noAuth) {
    const token = TokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers,
    body,
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("Content-Type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401 && !opts.skipAuthExpiredOn401) {
      TokenStore.clear();
      window.dispatchEvent(new CustomEvent("piggy:auth-expired"));
    }
    const message =
      (typeof payload === "object" && payload && "detail" in payload
        ? String((payload as { detail: unknown }).detail)
        : String(payload)) || `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, `${res.status}: ${message}`, payload);
  }

  return payload as T;
}
